import type { ThriveGameModule } from '../../../sdk';
import type { AssetId } from '../../../shared/types/ids';

/**
 * What the stage loads before a mission starts.
 *
 * (Was `preview-assets.ts`: the Phase 0 preview showed three tiles, so it picked three. A stage
 * running the module's own scenes has to have every ID those scenes might draw already in the
 * texture cache, because a scene never touches the loader itself.)
 *
 * **What the module says it needs first** — `AssetDefinition.preload` — rather than everything it
 * declares (D-097(4), closed in Slice E″). Loading the whole manifest was defensible while a theme
 * had seventeen entries; at a hundred and eighty-nine it made `preload` a field nothing read, and
 * a first screen that waits on the boss room's art is a first screen that waits.
 *
 * The platform still hard-codes no game's asset IDs: which pictures a mission needs before its
 * first question is a statement only the game can make, and this reads it rather than guessing.
 * The module's own tiles come too, because the shell draws them either way.
 */

/**
 * An ID no theme declares, included on purpose so the stage visibly proves the
 * unresolved-asset fallback rather than only asserting it in a test (D-014).
 */
export const DELIBERATELY_UNKNOWN_ASSET_ID: AssetId = 'hero.deliberately-unknown.v9';

export function selectStageAssetIds(gameModule: ThriveGameModule): AssetId[] {
  const chosen: AssetId[] = [];
  const add = (assetId: AssetId | undefined): void => {
    if (assetId === undefined || chosen.includes(assetId)) return;
    chosen.push(assetId);
  };

  add(gameModule.metadata.heroAssetId);
  add(gameModule.metadata.thumbnailAssetId);

  for (const definition of Object.values(gameModule.themeManifest.assets)) {
    if (definition.preload) add(definition.id);
  }
  for (const definition of Object.values(gameModule.themeManifest.ui)) {
    if (definition.preload) add(definition.id);
  }

  return [...chosen, DELIBERATELY_UNKNOWN_ASSET_ID];
}
